import { useEffect, useState } from 'react'
import moment from 'moment'

import {
  formatCurrency,
  formatNumber,
  getDefaultSeason,
  createWorkbook,
  addWorksheet,
  DEFAULTNUMHEADERS,
  saveWorkbook,
  setHeaders,
  setColumnsAndRows,
  numberFormat,
  currencyFormat,
  styleTotalRow,
} from '../../utilities/helpers'
import { getSalesReceivablesByCategory, getSeasons } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'

import { ReportActions } from '../ReportActions'
import { SeasonSelector } from '../SeasonSelector'
import { LoadingContainer } from '../LoadingContainer'
import { EmptyContainer } from '../EmptyContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'

const yearStart = () => moment().startOf('year').toDate()
const todayEnd = () => moment().endOf('day').toDate()

export default function SalesReceivablesByCategoryWrapper({ organization }) {
  const [seasons, setSeasons] = useState([])
  const [selectedSeason, setSelectedSeason] = useState('')
  const [fromDate, setFromDate] = useState(yearStart)
  const [toDate, setToDate] = useState(todayEnd)
  const [isLoadingSeasons, setIsLoadingSeasons] = useState(true)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)
  const [report, setReport] = useState(null)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    let cancelled = false

    const loadSeasons = async () => {
      if (!organization?.uuid) {
        setLoadError('Missing organization context for report.')
        setIsLoadingSeasons(false)
        return
      }

      setIsLoadingSeasons(true)
      setLoadError('')

      try {
        const seasonsRes = await getSeasons(organization.uuid)
        if (cancelled) return

        const loadedSeasons = seasonsRes?.data || []
        setSeasons(loadedSeasons)

        if (!loadedSeasons.length) {
          setLoadError('No seasons found for this organization.')
          setIsLoadingSeasons(false)
          return
        }

        const defaultSeason = getDefaultSeason(loadedSeasons)
        setSelectedSeason(
          String(defaultSeason?.id || loadedSeasons[0]?.id || '')
        )
      } catch (error) {
        if (cancelled) return
        console.error(
          'Error loading seasons for Sales & Receivables by Category:',
          error
        )
        setLoadError(
          'Unable to load seasons for Sales & Receivables by Category.'
        )
      } finally {
        if (!cancelled) setIsLoadingSeasons(false)
      }
    }

    loadSeasons()

    return () => {
      cancelled = true
    }
  }, [organization?.uuid])

  useEffect(() => {
    let cancelled = false

    const loadReport = async () => {
      if (!organization?.uuid || !selectedSeason || !fromDate || !toDate) return

      setIsLoadingData(true)
      setLoadError('')

      try {
        const res = await getSalesReceivablesByCategory(
          organization.uuid,
          selectedSeason,
          fromDate.toISOString(),
          toDate.toISOString()
        )
        if (cancelled) return
        setReport(res?.data || null)
      } catch (error) {
        if (cancelled) return
        console.error('Error loading Sales & Receivables by Category:', error)
        setLoadError('Unable to load Sales & Receivables by Category.')
        setReport(null)
      } finally {
        if (!cancelled) {
          setIsLoadingData(false)
          completeRefresh()
        }
      }
    }

    loadReport()

    return () => {
      cancelled = true
    }
  }, [organization?.uuid, selectedSeason, fromDate, toDate, refreshToken])

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates || []
    setFromDate(start || null)
    setToDate(end ? moment(end).endOf('day').toDate() : null)
  }

  const handleExcelExport = () => {
    if (!report?.categories?.length) return

    const seasonName = report?.season?.name || 'Season'
    const dateLabel = `${moment(fromDate).format('MMM D, YYYY')} – ${moment(
      toDate
    ).format('MMM D, YYYY')}`
    const workbook = createWorkbook()

    const categorySheet = addWorksheet('By Category', workbook)
    setHeaders(
      'Sales & Receivables by Category',
      organization?.name,
      categorySheet,
      [`Season: ${seasonName}`, `Date range: ${dateLabel}`]
    )

    const formatCategory = (row) => ({
      category: row.label,
      orders: row.orderCount,
      total_sales: row.totalSales,
      net_sales: row.netSales,
      payments_collected: row.paymentsCollected,
      outstanding_receivables: row.outstandingReceivables,
    })

    const categoryRows = [...report.categories, report.totals]
    setColumnsAndRows(
      Object.keys(formatCategory(categoryRows[0])),
      categoryRows,
      formatCategory,
      categorySheet
    )

    const categoryHeaderRows = DEFAULTNUMHEADERS + 2
    numberFormat(2, categorySheet, categoryHeaderRows)
    currencyFormat(3, categorySheet, categoryHeaderRows)
    currencyFormat(4, categorySheet, categoryHeaderRows)
    currencyFormat(5, categorySheet, categoryHeaderRows)
    currencyFormat(6, categorySheet, categoryHeaderRows)
    styleTotalRow(categorySheet.lastRow, categorySheet, false)

    if (report.flexPacks?.length) {
      const flexSheet = addWorksheet('Flex Pack Detail', workbook)
      setHeaders(
        'Flex Pack Sales & Receivables',
        organization?.name,
        flexSheet,
        [`Season: ${seasonName}`, `Date range: ${dateLabel}`]
      )

      const formatFlex = (row) => ({
        package_name: row.flexPackName,
        package_type: row.packageType,
        list_price: row.listPrice,
        quantity_sold: row.quantitySold,
        expected_sales: row.expectedSales,
        total_sales: row.totalSales,
        net_sales: row.netSales,
        payments_collected: row.paymentsCollected,
        outstanding_receivables: row.outstandingReceivables,
        price_variance: row.priceVariance,
      })

      setColumnsAndRows(
        Object.keys(formatFlex(report.flexPacks[0])),
        report.flexPacks,
        formatFlex,
        flexSheet
      )

      const flexHeaderRows = DEFAULTNUMHEADERS + 2
      currencyFormat(3, flexSheet, flexHeaderRows)
      numberFormat(4, flexSheet, flexHeaderRows)
      currencyFormat(5, flexSheet, flexHeaderRows)
      currencyFormat(6, flexSheet, flexHeaderRows)
      currencyFormat(7, flexSheet, flexHeaderRows)
      currencyFormat(8, flexSheet, flexHeaderRows)
      currencyFormat(9, flexSheet, flexHeaderRows)
      currencyFormat(10, flexSheet, flexHeaderRows)
    }

    saveWorkbook(workbook, 'sales receivables by category')
      .then()
      .catch((err) => alert(err.message))
  }

  if (isLoadingSeasons) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  return (
    <section className="spacer-sm">
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>Sales &amp; Receivables by Category</h1>
        </div>
        <p className="section-header-desc">
          Total sales per category less payments collected equals outstanding
          receivables. Use the date range for YTD collections and the flex pack
          detail to reconcile list price to actual sales.
        </p>
      </header>

      {loadError && !seasons.length ? (
        <Card body>
          <EmptyContainer style="center lg report">
            <p>{loadError}</p>
          </EmptyContainer>
        </Card>
      ) : seasons.length > 0 ? (
        <>
          <ReportActions
            isLoading={isLoadingData}
            onRefresh={refresh}
            isRefreshing={isLoadingData}
            selected={selectedSeason}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={handleDateRangeChange}
            data={report?.categories}
            downloadReport={handleExcelExport}
          >
            <SeasonSelector
              seasons={seasons}
              selected={selectedSeason}
              setSelected={setSelectedSeason}
              isLoading={isLoadingData}
            />
          </ReportActions>
          <Results
            selectedSeason={selectedSeason}
            isLoading={isLoadingData}
            report={report}
            loadError={loadError}
          />
        </>
      ) : (
        <Card body>
          <EmptyContainer style="center lg">
            <p>No seasons created</p>
            <p className="subtitle">
              Create a season to view sales and receivables by category.
            </p>
          </EmptyContainer>
        </Card>
      )}
    </section>
  )
}

function Results({ selectedSeason, isLoading, report, loadError }) {
  if (!selectedSeason) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Select a season to view sales and receivables</p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  if (loadError) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>{loadError}</p>
        </EmptyContainer>
      </Card>
    )
  }

  if (!report?.categories?.length) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No sales found for this season and date range</p>
        </EmptyContainer>
      </Card>
    )
  }

  const totals = report.totals || {}

  return (
    <>
      <Card body className="card--md mb-4">
        <Card.Title as="h5" className="mb-1">
          Sales by category
        </Card.Title>
        <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
          Total sales − payments collected = outstanding receivables
        </p>
        <div className="table-container border-bottom-0">
          <div className="table-container--fixed-height">
            <Table className="align-middle">
              <thead>
                <tr className="md">
                  <th className="uppercase text-body">Category</th>
                  <th className="text-center uppercase text-body">Orders</th>
                  <th className="text-end uppercase text-body">Total sales</th>
                  <th className="text-end uppercase text-body">Net sales</th>
                  <th className="text-end uppercase text-body">
                    Payments collected
                  </th>
                  <th className="text-end uppercase text-body">
                    Outstanding receivables
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.categories.map((row) => (
                  <tr key={row.key} className="md-x">
                    <td>{row.label}</td>
                    <td className="text-center">
                      {formatNumber(row.orderCount)}
                    </td>
                    <td className="text-end">
                      {formatCurrency(row.totalSales)}
                    </td>
                    <td className="text-end">{formatCurrency(row.netSales)}</td>
                    <td className="text-end">
                      {formatCurrency(row.paymentsCollected)}
                    </td>
                    <td className="text-end">
                      {formatCurrency(row.outstandingReceivables)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="md-x">
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td className="text-center">
                    <strong>{formatNumber(totals.orderCount)}</strong>
                  </td>
                  <td className="text-end">
                    <strong>{formatCurrency(totals.totalSales)}</strong>
                  </td>
                  <td className="text-end">
                    <strong>{formatCurrency(totals.netSales)}</strong>
                  </td>
                  <td className="text-end">
                    <strong>{formatCurrency(totals.paymentsCollected)}</strong>
                  </td>
                  <td className="text-end">
                    <strong>
                      {formatCurrency(totals.outstandingReceivables)}
                    </strong>
                  </td>
                </tr>
              </tfoot>
            </Table>
          </div>
        </div>
      </Card>

      <Card body className="card--md">
        <Card.Title as="h5" className="mb-1">
          Flex pack price reconciliation
        </Card.Title>
        <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
          Expected sales is list price × quantity sold. Price variance is actual
          total sales minus expected.
        </p>
        {!report.flexPacks?.length ? (
          <EmptyContainer style="center lg report">
            <p>No Flex Packages linked to this season</p>
          </EmptyContainer>
        ) : (
          <div className="table-container border-bottom-0">
            <div className="table-container--fixed-height">
              <Table className="align-middle">
                <thead>
                  <tr className="md">
                    <th className="uppercase text-body">Package</th>
                    <th className="text-left uppercase text-body">Type</th>
                    <th className="text-end uppercase text-body">List price</th>
                    <th className="text-center uppercase text-body">Qty</th>
                    <th className="text-end uppercase text-body">Expected</th>
                    <th className="text-end uppercase text-body">
                      Total sales
                    </th>
                    <th className="text-end uppercase text-body">Collected</th>
                    <th className="text-end uppercase text-body">
                      Outstanding
                    </th>
                    <th className="text-end uppercase text-body">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {report.flexPacks.map((row) => (
                    <tr
                      key={row.flexPackId || row.flexPackUuid}
                      className="md-x"
                    >
                      <td>{row.flexPackName}</td>
                      <td className="text-left">{row.packageType}</td>
                      <td className="text-end">
                        {formatCurrency(row.listPrice)}
                      </td>
                      <td className="text-center">
                        {formatNumber(row.quantitySold)}
                      </td>
                      <td className="text-end">
                        {formatCurrency(row.expectedSales)}
                      </td>
                      <td className="text-end">
                        {formatCurrency(row.totalSales)}
                      </td>
                      <td className="text-end">
                        {formatCurrency(row.paymentsCollected)}
                      </td>
                      <td className="text-end">
                        {formatCurrency(row.outstandingReceivables)}
                      </td>
                      <td className="text-end">
                        {formatCurrency(row.priceVariance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="md-x">
                    <td colSpan={3}>
                      <strong>Total</strong>
                    </td>
                    <td className="text-center">
                      <strong>
                        {formatNumber(report.flexPackTotals?.quantitySold)}
                      </strong>
                    </td>
                    <td className="text-end">
                      <strong>
                        {formatCurrency(report.flexPackTotals?.expectedSales)}
                      </strong>
                    </td>
                    <td className="text-end">
                      <strong>
                        {formatCurrency(report.flexPackTotals?.totalSales)}
                      </strong>
                    </td>
                    <td className="text-end">
                      <strong>
                        {formatCurrency(
                          report.flexPackTotals?.paymentsCollected
                        )}
                      </strong>
                    </td>
                    <td className="text-end">
                      <strong>
                        {formatCurrency(
                          report.flexPackTotals?.outstandingReceivables
                        )}
                      </strong>
                    </td>
                    <td className="text-end">
                      <strong>
                        {formatCurrency(report.flexPackTotals?.priceVariance)}
                      </strong>
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </div>
        )}
      </Card>
    </>
  )
}
